import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskedLine, EASE } from "./Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1527576539890-dfa815648363?q=80&w=2200&auto=format&fit=crop";

const CRANE_PATHS = [
  "M140 480 L224 480",
  "M165 480 L165 112",
  "M199 480 L199 112",
  "M165 444 L199 404 L165 364 L199 324 L165 284 L199 244 L165 204 L199 164 L165 124",
  "M150 112 L214 112",
  "M182 106 L372 106",
  "M182 106 L92 106",
  "M92 106 L92 136 L122 136 L122 106",
  "M182 106 L182 58",
  "M182 58 L372 106",
  "M182 58 L92 106",
  "M322 106 L322 226",
  "M312 226 L332 226 L322 248 Z",
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const craneY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen flex flex-col justify-end overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src={HERO_IMG}
          alt="Architectural structure"
          className="w-full h-[130%] object-cover grayscale opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-[#050505]/30" />
      </motion.div>

      <motion.svg
        viewBox="0 0 400 500"
        className="absolute right-[-4%] md:right-[4%] top-[8%] h-[70vh] w-auto opacity-60 pointer-events-none"
        style={{ y: craneY }}
        fill="none"
        stroke="rgba(255,255,255,0.28)"
        strokeWidth="2"
        data-testid="hero-crane-illustration"
      >
        {CRANE_PATHS.map((d, i) => (
          <motion.path
            key={i}
            d={d}
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1.6, ease: EASE, delay: 0.9 + i * 0.12 }}
          />
        ))}
      </motion.svg>

      <motion.div className="relative z-10 px-6 md:px-12 pb-10 md:pb-14 w-full" style={{ opacity: contentOpacity }}>
        <MaskedLine delay={0.15}>
          <span className="inline-block border border-white/30 px-3 py-1.5 text-[10px] md:text-xs tracking-[0.35em] uppercase text-ash mb-8" data-testid="hero-eyebrow">
            ETMB Design Build PLLC — Architecture + Construction
          </span>
        </MaskedLine>

        <h1 className="font-display font-bold uppercase leading-[0.92] tracking-tight text-[clamp(3.8rem,12vw,11rem)]" data-testid="hero-headline">
          <MaskedLine delay={0.3}>We Design.</MaskedLine>
          <MaskedLine delay={0.45}>We Build.</MaskedLine>
          <MaskedLine delay={0.6}>
            <span className="text-[#4361C2]" data-testid="hero-headline-accent">
              One Team.
            </span>
          </MaskedLine>
        </h1>

        <div className="mt-10 md:mt-14 flex flex-col md:flex-row md:items-end justify-between gap-8 border-t border-white/15 pt-8">
          <MaskedLine delay={0.85}>
            <p className="max-w-md text-sm md:text-base font-light leading-relaxed text-ash" data-testid="hero-subtext">
              A licensed design-build practice. Architect, engineer, and builder at the same table from the first sketch to the final walkthrough.
            </p>
          </MaskedLine>
          <MaskedLine delay={1}>
            <div className="flex items-center gap-10 text-xs tracking-[0.25em] uppercase text-ash">
              <span data-testid="hero-stat-projects">120+ Projects</span>
              <span data-testid="hero-stat-years">20+ Yrs</span>
              <span className="flex items-center gap-2 text-paper" data-testid="hero-scroll-hint">
                Scroll <ArrowDown size={14} className="animate-bounce" />
              </span>
            </div>
          </MaskedLine>
        </div>
      </motion.div>
    </section>
  );
};
